/**
 * Media attachments on webchat messages.
 *
 * For our own storage the backend does NOT send a ready-to-use URL. It sends
 * a short-lived `media_token` and the widget composes the URL against the same
 * `apiBaseUrl` it already uses for the handshake, the history fetch and the
 * socket. That split is deliberate: the widget runs embedded on the
 * customer's domain, so a relative URL from the backend would resolve
 * against the customer's site, and an absolute one would force the backend
 * to know its own public hostname.
 *
 * See mirlo-messages-service `webchat-webhook.controller.ts` (the media
 * endpoint) and `utils/media-token.ts` (why the credential is in the query
 * string rather than a header).
 *
 * The exception is an attachment outside our storage (a catalog photo the
 * agent sent by the store's own URL): it is public already, so it arrives as
 * `media_url` and the widget loads it straight from there.
 */

import type { MediaKind, WireMediaFile } from './api-types';

/** Media attachment in the shape the widget renders. */
export interface MediaAttachment {
  kind: MediaKind;
  /** Original filename, when the channel provided one. */
  name?: string;
  mimeType: string;
  /** Size in bytes, when known. */
  size?: number;
  /** Absolute, credential-bearing URL of the bytes. */
  url: string;
}

/** Kinds the widget knows how to label. Anything else degrades to a file. */
const KNOWN_KINDS: MediaKind[] = ['image', 'video', 'audio', 'document'];

export function buildMediaUrl(
  apiBaseUrl: string,
  messageId: string,
  mediaToken: string,
): string {
  const base = apiBaseUrl.replace(/\/+$/, '');
  return `${base}/api/v1/webchat/media/${encodeURIComponent(
    messageId,
  )}?t=${encodeURIComponent(mediaToken)}`;
}

/**
 * Shape of a message as it arrives on the wire, narrowed to the media
 * fields. Both the history response and the `agent_message` socket event
 * carry these, so one mapper serves both.
 */
interface WireMediaMessage {
  id: string;
  kind?: string;
  file?: WireMediaFile;
  media_token?: string;
  media_url?: string;
}

/**
 * Public URL of an attachment outside Mirlo's storage — a catalog photo the
 * agent sent by the store's own URL. The backend only forwards `https`, but
 * this lands in an `src`/`href`, so it is checked again here.
 */
function externalUrl(mediaUrl: string | undefined): string | undefined {
  if (!mediaUrl) return undefined;
  try {
    return new URL(mediaUrl).protocol === 'https:' ? mediaUrl : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Builds the internal attachment, or `undefined` when the message carries no
 * media. Requires a file descriptor plus a way to load the bytes: a token
 * (the backend only mints one for objects it can actually serve) or a public
 * `media_url`. A message missing both would render a bubble that can never
 * load.
 */
export function toMediaAttachment(
  apiBaseUrl: string,
  message: WireMediaMessage,
): MediaAttachment | undefined {
  const { id, kind, file, media_token } = message;
  const url = media_token
    ? buildMediaUrl(apiBaseUrl, id, media_token)
    : externalUrl(message.media_url);
  if (!file || !url) return undefined;

  return {
    kind: KNOWN_KINDS.includes(kind as MediaKind)
      ? (kind as MediaKind)
      : 'document',
    ...(file.name ? { name: file.name } : {}),
    mimeType: file.mime_type || 'application/octet-stream',
    ...(file.size ? { size: file.size } : {}),
    url,
  };
}

/**
 * SVG is an image the browser will happily render — and it can execute
 * script. The media endpoint already refuses to serve it inline, so
 * previewing it would work around our own defence.
 */
const NEVER_INLINE = ['image/svg+xml'];

/**
 * Whether the attachment renders as a fixed-ratio preview box (image, video)
 * rather than a card or a thin player.
 *
 * The bubble needs to know this BEFORE the media renders: a boxed preview
 * has to stretch the bubble to its full allowed width, otherwise the box has
 * no width to compute its reserved height from.
 */
export function isBoxedPreview(attachment: MediaAttachment): boolean {
  return (
    !NEVER_INLINE.includes(attachment.mimeType) &&
    (attachment.kind === 'image' || attachment.kind === 'video')
  );
}

/** Whether the browser can render this attachment inline at all. */
export function isPreviewable(attachment: MediaAttachment): boolean {
  return isBoxedPreview(attachment) || attachment.kind === 'audio';
}

/** Human-readable size for the attachment card. Empty when unknown. */
export function formatFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;

  const kb = bytes / 1024;
  if (kb < 1024) return `${trim(kb)} KB`;
  return `${trim(kb / 1024)} MB`;
}

function trim(value: number): string {
  // One decimal, but never a trailing ".0" — "2 KB" reads better than "2.0 KB".
  return value.toFixed(1).replace(/\.0$/, '');
}
